import React, { useMemo, useEffect, useState } from "react";
import { Button } from "./button";
import { LoadingOverlay } from "./loader";
import { Upload, Plus } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { globalStorage } from "../../utils/globalStorage";
import { cloneDeep } from "lodash";
import {createDocumentResponseLocally,extensionList,assignDocIdAndPath,upload,validateFilesFormat,LoaderSection} from './dragdropHelper';
import PropTypes from "prop-types";

const applicationData = globalStorage.getItem("APPLICATION_DATA");

const DragAndDrop = (props) => {
  const {
    loanId,
    isReadOnly,
    addMultipleDocumentsAPI={},
    storeLocalOptions = {},
    remoteFetch,
    documentType,
    parentType,
    parentId = "",
    updateLoacalContextOnUpload = false,
    checkFilesFormat = true,
    dropZoneOptions = {},
    overRideFileFormat = null,
    maxFiles = null,
    postResponseOnUploadDoc = null,
    generatePreSignedUrl,
    uploadToAwsS3,
    toastError,
    allowedExtension=extensionList,
    supportedFormatsText = "PDF, DOC, XLS, JPG, PNG (Max 500MB)",
    showLoaderSection = true
  } = props;
  
  const [prevAcceptedFiles, setPrevAcceptedFiles] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [progressInfos, setProgressInfos] = useState([]);
  const [startUpload, setStartUpload] = useState(false);
  const [isAllFilesUploaded, setIsAllFilesUploaded] = useState(false);

  useEffect(() => {
    if (errorMsg === "") return;
    toastError("Error", errorMsg, setErrorMsg(''));
  },[errorMsg])
  
  useEffect(() => {
      const filteredFiles = progressInfos.filter((file) => {
        return (file.message !== "Uploaded successfully" && file.percentage < 100)
      });
    }, [progressInfos]);

  const {
    setLocalUploadedFiles = () => {},
    localUploadedFiles = [],
    localContext = { documents: [] },
    handleUpdateDocumentContext = () => {},
    contextKey = "documents"
  } = storeLocalOptions;

  const onDrop = (acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      toastError(
        "Error",
        fileRejections?.[0]?.errors?.[0]?.message,
        ()=>{}
      )
    }
  };
  
  const { getRootProps, getInputProps, acceptedFiles } =
      useDropzone({
        noClick: false,
        disabled: isReadOnly,
        noKeyboard: true,
        maxFiles,
        onDrop,
        ...dropZoneOptions
      });
  
    const storeFileDetailsLocally = () => {
      const fileValidations = validateFilesFormat(acceptedFiles,allowedExtension, setErrorMsg);
      if (!fileValidations) {
        const files = createDocumentResponseLocally(
          acceptedFiles,
          localContext,
          contextKey,
          documentType,
          parentType,
          parentId
        );
        const currentDocuments = [...localContext[contextKey], ...files];
        handleUpdateDocumentContext(
          contextKey,
          currentDocuments,
          "",
          localContext
        );
        const allAcceptedFilesToUpload = [...prevAcceptedFiles, ...acceptedFiles];
        const tempProgressInfos = acceptedFiles.map((file) => {
          return {
            percentage: 0,
            fileName: file.name,
            message: "Uploading...",
            fileSize: file.size,
            description: "",
            path: null,
            isUploaded: null
          };
        });
        setProgressInfos(tempProgressInfos);
        setLocalUploadedFiles(allAcceptedFilesToUpload);
        setPrevAcceptedFiles(allAcceptedFilesToUpload);
      }
    };
  
    useEffect(() => {
      if (acceptedFiles.length === 0) return;
      if (checkFilesFormat) {
        const fileValidations = validateFilesFormat(acceptedFiles,allowedExtension, setErrorMsg);
        if (fileValidations) return;
      }
      if (parentId !== "") {
        const allAcceptedFilesToUpload = [
          ...localUploadedFiles,
          ...acceptedFiles
        ];
        setLocalUploadedFiles(allAcceptedFilesToUpload);
        const tempProgressInfos = acceptedFiles.map((file) => {
          return {
            percentage: 0,
            fileName: file.name,
            message: "Uploading...",
            description: "",
            fileSize: file.size,
            path: null,
            isUploaded: null
          };
        });
        setProgressInfos(tempProgressInfos);
        setStartUpload(true);
        setIsAllFilesUploaded(false);
        return;
      }
      storeFileDetailsLocally();
    }, [acceptedFiles]);
  
    useEffect(() => {
      if (!startUpload) return;
      acceptedFiles.forEach((file, idx) => {
        upload({
          idx,
          file,
          progressInfos,
          setProgressInfos,
          loanId,
          overRideFileFormat,
          generatePreSignedUrl: generatePreSignedUrl,
          uploadToAwsS3: uploadToAwsS3
        });
      
        });
      
      setStartUpload(false);
    }, [startUpload]);
  
    const isReadyToProcessUpload = () => {
      if (isAllFilesUploaded) return false;
      if (!progressInfos?.length) return false;
      return !progressInfos.some((progressInfo) => {
        return progressInfo?.isUploaded === null;
      });
    };
  
    const filterUploadedDocs = (documents) => {
      return documents?.reduce((acc, doc, index) => {
        if (progressInfos[index]?.isUploaded) {
          acc.push({
            ...doc,
            path: progressInfos[index].path
          });
        }
        return acc;
      }, []);
    };
  
    const updateProgressInfosWithApiData = (apiData) => {
      const files = cloneDeep(progressInfos);
      return files?.map((document) => {
        const filteredDoc = apiData.find((doc) => {
          return doc.path === document.path;
        });
        if (filteredDoc) {
          document.documentId = filteredDoc.documentId;
          document.createdOn = filteredDoc.createdOn;
        }
        return document;
      });
    };
  
    const handleApiSuccess = (data) => {
      if (data?.data) {
        setProgressInfos(updateProgressInfosWithApiData(data.data));
        setIsAllFilesUploaded(true);
      }
      remoteFetch();
      if (!updateLoacalContextOnUpload) return;
      const files = assignDocIdAndPath(
        data?.data,
        localContext,
        contextKey,
        documentType,
        parentType,
        parentId
      );
      const currentDocuments = [...localContext[contextKey], ...files];
      handleUpdateDocumentContext(contextKey, currentDocuments, "", localContext);
    };
  
    const handleApiError = () => {
      toastError(
        "Error",
        "Error while associating uploaded document to parent.",
        ()=>{}
      );
    };
  
    useEffect(() => {
      if (!isReadyToProcessUpload()) return;
      const documents = createDocumentResponseLocally(
        acceptedFiles,
        localContext,
        contextKey,
        documentType,
        parentType,
        parentId
      );
      const updatedDocs = filterUploadedDocs(documents);
      if (updatedDocs?.length > 0) {
        if (postResponseOnUploadDoc) {
          postResponseOnUploadDoc(updatedDocs);
        } else {
          addMultipleDocumentsAPI.mutate(updatedDocs, {
            onSuccess: handleApiSuccess,
            onError: handleApiError
          });
        }
      }
    }, [progressInfos]);
  
    const isLoading = useMemo(() => {
      return addMultipleDocumentsAPI.isLoading;
    }, [addMultipleDocumentsAPI.isLoading]);    
    const fileLabel = maxFiles === 1 ? "File" : "Files";
  
  return (
    <>
    {isLoading && (
        <LoadingOverlay
          isVisible={isLoading}
          variant="spinner"
          size="lg"
          message="Loading..."
        />
      )}
      <div className='min-w-100 p-6 border-2 border-dashed border-gray-300 rounded-md text-center cursor-pointer bg-[var(--color-neutral-1)]'>
        <div {...getRootProps()} className='flex flex-col gap-4'>
          <div className='flex flex-col items-center rounded'>
            <div className='w-[80px] h-[80px] rounded-full flex items-center justify-center bg-[var(--background-page)]'>
              <Upload className='w-5 h-5 text-[var(--color-primary-3)]' />
            </div>
          </div>
          <div className='flex flex-col items-center gap-2'>
            <p className="font-semibold text-base">Drag & drop files here </p>
            <p className="text-xs text-gray-600">or click the button to browse your files</p>
          </div>
          <div>
              <Button disabled={isReadOnly} variant='primary'>
                  Add Files
                  <Plus className='w-5 h-5' />
              </Button>
              <input type='file' {...getInputProps()}/>
            </div>
          <div className='flex flex-col items-center'>
          <p className="text-xs text-[var(--color-neutral-8)]">{supportedFormatsText}</p>
          </div>
        </div>
      </div>
      {showLoaderSection && (
        <LoaderSection progressInfos={progressInfos} />
      )}
  </>
  );
}

DragAndDrop.propTypes = {
  loanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isReadOnly: PropTypes.bool,
  addMultipleDocumentsAPI: PropTypes.shape({
    mutate: PropTypes.func,
    isLoading: PropTypes.bool,
  }),
  storeLocalOptions: PropTypes.shape({
    setLocalUploadedFiles: PropTypes.func,
    localUploadedFiles: PropTypes.array,
    localContext: PropTypes.object,
    handleUpdateDocumentContext: PropTypes.func,
    contextKey: PropTypes.string,
  }),
  remoteFetch: PropTypes.func,
  documentType: PropTypes.string,
  parentType: PropTypes.string,
  parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  updateLoacalContextOnUpload: PropTypes.bool,
  checkFilesFormat: PropTypes.bool,
  dropZoneOptions: PropTypes.object,
  overRideFileFormat: PropTypes.any,
  maxFiles: PropTypes.number,
  postResponseOnUploadDoc: PropTypes.func,
  generatePreSignedUrl: PropTypes.func,
  uploadToAwsS3: PropTypes.func,
  toastError: PropTypes.func,
  allowedExtension: PropTypes.arrayOf(PropTypes.string),
  supportedFormatsText: PropTypes.string,
  showLoaderSection: PropTypes.bool,
};

export { DragAndDrop };